import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  input,
  output,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import {
  ACC_BOOLEAN_DISPLAY_MAP,
  ACC_EMPLOYMENT_STATUS_MAP,
  ACC_ETHNICITY_MAP,
  ACC_GEOLOCATIONS_MAP,
  ACC_OCCUPATION_ENTRIES,
  ACC_PROVIDER_TYPE_MAP,
  ACC_REHABILITATION_DISPLAY_MAP,
  ACC_REHABILITATION_MAP,
  ACC_SCENE_MAP,
  ACC_SPORTS_MAP,
  ACC_WORK_TYPE_MAP,
  AccAlveolusInjury,
  AccBooleanValue,
  AccCariesActivity,
  AccCondition,
  AccDeglovingInjury,
  AccGender,
  AccIncapacityType,
  AccJawInjury,
  AccPhysicalRestriction,
  AccProsthesisType,
  AccSoftTissueHow,
  AccTmjInjury,
  AccToothInjuryClassification,
  AccToothPreAccidentCondition,
  IAccClaimProviderDetails,
  IAccDateRange,
} from '@principle-theorem/principle-core/interfaces';
import {
  DAY_MONTH_YEAR_FORMAT,
  ISelectOption,
  ISODateType,
  splitCamel,
  toMoment,
} from '@principle-theorem/shared';
import { upperFirst } from 'lodash';
import { AccDiagnosisDetailsFormGroup } from '../../form-groups/acc-dental-diagnosis.formgroup';
import { AccEmploymentFormGroup } from '../../form-groups/acc-employment.formgroup';
import {
  AccAssistanceFormGroup,
  AccInjuryFormGroup,
} from '../../form-groups/acc-injury.formgroup';
import { AccPatientFormGroup } from '../../form-groups/acc-patient.formgroup';
import { AccReferralManagementFormGroup } from '../../form-groups/acc-referrals.formgroup';
import { AccWorkCapacityFormGroup } from '../../form-groups/acc-work-capacity.formgroup';
import { AccControlSummaryComponent } from '../acc-control-summary/acc-control-summary.component';
import { AccPatientReferenceFormGroup } from '../../form-groups/acc-patient-referance.formgroup';

@Component({
  selector: 'pr-acc-form-summary',
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    AccControlSummaryComponent,
  ],
  templateUrl: './acc-form-summary.component.html',
  styleUrls: ['./acc-form-summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccFormSummaryComponent {
  patientReferenceForm = input.required<AccPatientReferenceFormGroup>();
  patientForm = input.required<AccPatientFormGroup>();
  injuryForm = input.required<AccInjuryFormGroup>();
  employmentForm = input.required<AccEmploymentFormGroup>();
  workCapacityForm = input.required<AccWorkCapacityFormGroup>();
  dentalDiagnosisForm = input.required<AccDiagnosisDetailsFormGroup>();
  referralForm = input.required<AccReferralManagementFormGroup>();
  assistanceForm = input.required<AccAssistanceFormGroup>();
  editStep = output<number>();

  readonly accBooleanValue = AccBooleanValue;
  readonly accGender = AccGender;
  readonly accEthnicityMap = ACC_ETHNICITY_MAP;
  readonly accBooleanDisplayMap = ACC_BOOLEAN_DISPLAY_MAP;
  readonly accGeolocationsMap = ACC_GEOLOCATIONS_MAP;
  readonly accSceneMap = ACC_SCENE_MAP;
  readonly accSportsMap = ACC_SPORTS_MAP;
  readonly accRehabilitationMap = ACC_REHABILITATION_MAP;
  readonly accRehabilitationDisplayMap = ACC_REHABILITATION_DISPLAY_MAP;
  readonly accOccupationEntries = ACC_OCCUPATION_ENTRIES;
  readonly accEmploymentStatusMap = ACC_EMPLOYMENT_STATUS_MAP;
  readonly accWorkTypeMap = ACC_WORK_TYPE_MAP;
  readonly accProviderTypeMap = ACC_PROVIDER_TYPE_MAP;
  readonly accIncapacityType = AccIncapacityType;
  readonly accPhysicalRestriction = AccPhysicalRestriction;
  readonly accProsthesisType = AccProsthesisType;
  readonly accSoftTissueHow = AccSoftTissueHow;
  readonly accDeglovingInjury = AccDeglovingInjury;
  readonly accAlveolusInjury = AccAlveolusInjury;
  readonly accJawInjury = AccJawInjury;
  readonly accTmjInjury = AccTmjInjury;
  readonly accCondition = AccCondition;
  readonly accCariesActivity = AccCariesActivity;
  readonly accToothInjuryClassification = AccToothInjuryClassification;
  readonly accToothPreAccidentCondition = AccToothPreAccidentCondition;

  dateFormatFn = (date: ISODateType): string => {
    return toMoment(date).format(DAY_MONTH_YEAR_FORMAT);
  };

  dateRangeFormatFn = (range: IAccDateRange): string => {
    return `${this.dateFormatFn(range.fromDate)} - ${this.dateFormatFn(
      range.toDate
    )}`;
  };

  splitCamelCase = (str: string): string => {
    return upperFirst(splitCamel(str));
  };

  splitStringArray = (str: string[]): string => {
    return str.map((item) => this.splitCamelCase(item)).join(', ');
  };

  splitToothArray = (value: number[]): string => {
    return value.join(', ');
  };

  optionDisplayFn = (option: ISelectOption<unknown>): string => {
    return option.label || '';
  };

  providerDisplayFn = (provider: IAccClaimProviderDetails): string => {
    return `${provider.firstName} ${provider.surname} (${provider.providerId})`;
  };

  editPatientReference(): void {
    this.editStep.emit(0);
  }

  editPatientDetails(): void {
    this.editStep.emit(1);
  }

  editAccidentDetails(): void {
    this.editStep.emit(2);
  }

  editEmploymentDetails(): void {
    this.editStep.emit(3);
  }

  editDiagnosisDetails(): void {
    this.editStep.emit(4);
  }

  editOtherAssistance(): void {
    this.editStep.emit(5);
  }
}
